import { Cardinality } from '@/Enums/Enums';
import type {
    MetadataEditor,
    DynamicAssoziationSide,
    Dictionary,
} from '@/Typings';
import {
    ResourceManager,
    Loader,
    Utilities,
    ViewViewModel,
    RADEnvironmentProxy,
    LocatorHelper,
    LabelViewModel,
    ImageViewModel,
    ButtonViewModel,
    WindowViewModel,
    TextBoxViewModel,
    DropDownListViewModel,
    FilterHelper,
    GlobalConfiguration,
    NavigationService,
} from 'radpresentation.htmlclient.defaultviewextensions';

class CreateDynamicAssociationViewViewModel extends ViewViewModel {
    // html bindings
    public okay: ButtonViewModel;
    public cancel: ButtonViewModel;
    private window: WindowViewModel;

    private nameLabel: LabelViewModel;
    private name: TextBoxViewModel;

    private aToBLabel: LabelViewModel;
    private aToB: TextBoxViewModel;
    private bToALabel: LabelViewModel;
    private bToA: TextBoxViewModel;

    private targetTypeLabel: LabelViewModel;
    private targetType: DropDownListViewModel;

    private cardinality_B_To_A_Label: LabelViewModel;
    private cardinality_B_To_A: DropDownListViewModel;
    private cardinality_A_To_B_Label: LabelViewModel;
    private cardinality_A_To_B: DropDownListViewModel;

    // properties
    private taken_AtoB_Names: Array<string>;
    private taken_BtoA_Names: Array<string>;
    private taken_DA_Names: Array<string>;

    private taken_BtoA_NamesByTargetType: Dictionary<Array<string>>;

    // by params
    public metaData: MetadataEditor;

    // injected
    public yesCallback: (
        name: string,
        aToBName: string,
        bToAName: string,
        targetType: string,
        cardinality_A_To_B: number,
        cardinality_B_To_A: number
    ) => void;

    constructor(params: any) {
        super(params);

        this.metaData = params.metaData;
        this.viewFactory = params.viewFactory;

        this.createNameTextBox();
        this.create_AtoB_TextBox();
        this.create_BtoA_TextBox();
        this.createTargetTypeDropDown();
        this.createCardinalityDropDowns();

        this.createWindow();
        this.createOkayButton();
        this.createCancelButton();

        this.initTakenNames();

        this.defineSubscription(this.targetType.value, value =>
            this.loadTargetTypeAssociationNames(
                this.targetType.selected()['pdoId']
            )
        );
        this.defineComputed(() =>
            this.okay.isEnabled(this.evaluateOkayButtonEnabledState())
        );
    }

    private createNameTextBox() {
        this.nameLabel = LabelViewModel.create(
            'RAD.RADFOUNDATION.PROBLEMDOMAIN.META.DYNAMICASSOZIATION_NAME_ERGNAME'
        );
        this.name = new TextBoxViewModel({
            locator: LocatorHelper.buildLocator(this.locator, 'name'),
        });
    }

    private create_AtoB_TextBox() {
        this.aToBLabel = LabelViewModel.create(
            'RAD.RADFOUNDATION.PROBLEMDOMAIN.META.DYNAMICASSOZIATION_TO_B_ERGNAME'
        );
        this.aToB = new TextBoxViewModel({
            locator: LocatorHelper.buildLocator(this.locator, 'to_b'),
        });
    }

    private create_BtoA_TextBox() {
        this.bToALabel = LabelViewModel.create(
            'RAD.RADFOUNDATION.PROBLEMDOMAIN.META.DYNAMICASSOZIATION_TO_A_ERGNAME'
        );
        this.bToA = new TextBoxViewModel({
            locator: LocatorHelper.buildLocator(this.locator, 'to_a'),
        });
    }

    private createTargetTypeDropDown() {
        this.targetTypeLabel = LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.DYNAMICASSOCIATION_TARGETTYPE'
        );
        this.targetType = new DropDownListViewModel({
            data: (ko as any).asyncComputed(() => {
                return $.Deferred(() => Loader.getInstance().show())
                    .resolve()
                    .then(() => {
                        return RADEnvironmentProxy.getInstance().createIteratorWithOperator(
                            {
                                pdTypeName:
                                    'RAD.RADFoundation.ProblemDomain.Meta.PDTypeMeta',
                                properties: ['Name'],
                                filter: JSON.stringify(
                                    FilterHelper.createFilterObjectForStaticFilter(
                                        'this.IsExtensible = true'
                                    )
                                ),
                                orderby: 'this.Name',
                                retrieveExtentCount: true,
                                contextPDO_ID: '-1',
                                itOperator: this.defaultIteratorOperator,
                            }
                        );
                    })
                    .then(iterator => this.getDataExtend(iterator))
                    .then(datasets => this.transformToDataStructure(datasets))
                    .always(() => Loader.getInstance().hide());
            }, []),
            dataTextField: 'name',
            dataValueField: 'name',
            isEnabled: true,
            locator: LocatorHelper.buildLocator(this.locator, 'target_type'),
        });
    }

    private createCardinalityDropDowns() {
        let uiGeneratorData = [
            {
                caption: ResourceManager.getInstance().loadResource(
                    'RAD.RADFOUNDATION.PROBLEMDOMAIN.META.PDASSOZIATIONCARDINALITYET_ONE_ERGNAME',
                    true
                ),
                value: Cardinality.One,
            },
            {
                caption: ResourceManager.getInstance().loadResource(
                    'RAD.RADFOUNDATION.PROBLEMDOMAIN.META.PDASSOZIATIONCARDINALITYET_MANY_ERGNAME',
                    true
                ),
                value: Cardinality.Many,
            },
        ];

        this.cardinality_A_To_B_Label = new LabelViewModel({
            caption: 'Cardinality A to B',
        });
        this.cardinality_A_To_B = new DropDownListViewModel({
            data: uiGeneratorData,
            dataTextField: 'caption',
            dataValueField: 'value',
            isEnabled: true,
            locator: LocatorHelper.buildLocator(
                this.locator,
                'to_a_cardinality'
            ),
        });
        this.cardinality_B_To_A_Label = new LabelViewModel({
            caption: 'Cardinality B to A',
        });
        this.cardinality_B_To_A = new DropDownListViewModel({
            data: uiGeneratorData,
            dataTextField: 'caption',
            dataValueField: 'value',
            isEnabled: true,
            locator: LocatorHelper.buildLocator(
                this.locator,
                'to_b_cardinality'
            ),
        });

        this.cardinality_A_To_B.value(Cardinality.One);
        this.cardinality_B_To_A.value(Cardinality.One);
    }

    private createWindow() {
        this.window = new WindowViewModel({
            locator: LocatorHelper.buildLocator(this.locator, 'window'),
            isOpen: true,
            width: 600,
            isModal: true,
            allowClose: false,
            headline: LabelViewModel.create(
                'RADEM.RADEXTENSIONMANAGER.CREATE_DYNAMIC_ASSOCIATION'
            ),
            onClose: () => {
                NavigationService.getInstance().removeViewFactoryIsolated(
                    this.viewFactory
                );
            },
        });
    }

    private createOkayButton() {
        const buttonLocator = LocatorHelper.buildLocator(
            this.locator,
            'button.okay'
        );
        const buttonImageLocator = LocatorHelper.buildLocator(
            buttonLocator,
            'image'
        );
        const buttonLabelLocator = LocatorHelper.buildLocator(
            buttonLocator,
            'label'
        );

        this.okay = new ButtonViewModel({
            locator: buttonLocator,
            image: new ImageViewModel({
                locator: buttonImageLocator,
                source: GlobalConfiguration.inlineImages.WINDOW_OK,
                alternativeText: new LabelViewModel({
                    ergName: 'OKAY',
                }),
            }),
            label: new LabelViewModel({
                locator: buttonLabelLocator,
                ergName: 'OKAY',
            }),
            onClick: () => {
                if (this.okay.isEnabled()) {
                    this.aToB.value(this.aToB.value().trim());
                    this.bToA.value(this.bToA.value().trim());

                    let aToBValue = this.aToB.value().toLowerCase();
                    let bToAValue = this.bToA.value().toLowerCase();
                    let hasWhitespacesErrorMessage = this.loadResource(
                        'RADEM.RADEXTENSIONMANAGER.NOWHITESPACES_CONSTRAINT'
                    );

                    this.aToB.isValid(!this.hasWhiteSpace(aToBValue));
                    this.aToB.invalidationText(
                        this.hasWhiteSpace(aToBValue)
                            ? hasWhitespacesErrorMessage
                            : null
                    );

                    this.bToA.isValid(!this.hasWhiteSpace(bToAValue));
                    this.bToA.invalidationText(
                        this.hasWhiteSpace(bToAValue)
                            ? hasWhitespacesErrorMessage
                            : null
                    );

                    if (this.aToB.isValid() && this.bToA.isValid()) {
                        this.yesCallback(
                            this.name.value(),
                            this.aToB.value(),
                            this.bToA.value(),
                            this.targetType.value(),
                            this.cardinality_A_To_B.value(),
                            this.cardinality_B_To_A.value()
                        );
                        this.window.isOpen(false);
                    }
                }
            },
            isEnabled: false,
        });
    }

    private createCancelButton() {
        const buttonLocator = LocatorHelper.buildLocator(
            this.locator,
            'button.cancel'
        );
        const buttonImageLocator = LocatorHelper.buildLocator(
            buttonLocator,
            'image'
        );
        const buttonLabelLocator = LocatorHelper.buildLocator(
            buttonLocator,
            'label'
        );

        this.cancel = new ButtonViewModel({
            locator: buttonLocator,
            image: new ImageViewModel({
                locator: buttonImageLocator,
                source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                alternativeText: new LabelViewModel({
                    ergName: 'CANCEL',
                }),
            }),
            label: new LabelViewModel({
                locator: buttonLabelLocator,
                ergName: 'CANCEL',
            }),
            onClick: () => {
                this.window.isOpen(false);
            },
        });
    }

    private initTakenNames() {
        let metaData = this.metaData;
        let pdType = metaData.Types[metaData.SubjectPDTypeMeta];
        let dynamicAssociations = Object.keys(metaData.DynamicAssoziations).map(
            key => metaData.DynamicAssoziations[key]
        );

        this.taken_DA_Names = _.pluck(dynamicAssociations, 'Name');
        this.taken_AtoB_Names = _.pluck(pdType.Assoziations, 'Name').concat(
            _.pluck(pdType.Properties, 'Name')
        );
        this.taken_BtoA_Names = []; // calculated when target type is selected (whether initial or as an update)

        this.taken_BtoA_NamesByTargetType = {};
        _.pluck(dynamicAssociations, 'Target').forEach(
            (target: DynamicAssoziationSide) => {
                if (
                    !_.contains(
                        Object.keys(this.taken_BtoA_NamesByTargetType),
                        target.Type
                    )
                ) {
                    this.taken_BtoA_NamesByTargetType[
                        target.Type.toLowerCase()
                    ] = [];
                }
                this.taken_BtoA_NamesByTargetType[
                    target.Type.toLowerCase()
                ].push(target.Name.toLowerCase());
            }
        );
    }

    private loadTargetTypeAssociationNames(targetTypePdoId: string) {
        $.Deferred(() => {
            this.okay.isEnabled(false);
            Loader.getInstance().show();
        })
            .resolve()
            .then(() => {
                return RADEnvironmentProxy.getInstance().createIteratorForAssoziation(
                    {
                        pdTypeName:
                            'RAD.RADFoundation.ProblemDomain.Meta.PDTypeMeta',
                        pdo_id: targetTypePdoId as any,
                        assoziationName: 'Assoziations',
                        properties: ['Name'],
                        filter: JSON.stringify(
                            FilterHelper.createFilterObjectForStaticFilter('')
                        ),
                        orderby: 'this.Name',
                        retrieveExtentCount: true,
                        itOperator: this.defaultIteratorOperator,
                    }
                );
            })
            .then(iterator => this.getDataExtend(iterator))
            .then(datasets => this.transformToDataStructure(datasets))
            .then(
                datasets =>
                    (this.taken_BtoA_Names = datasets.map(ds => ds.name))
            )
            .always(() => {
                this.okay.isEnabled(this.evaluateOkayButtonEnabledState());
                Loader.getInstance().hide();
            });
    }

    private getDataExtend(iterator): JQueryDeferred<any> | void {
        if (!this.isDisposed) {
            return RADEnvironmentProxy.getInstance().getExtentWithPDOIDAsFirstColumn(
                {
                    iteratorID: iterator.id,
                    start: 0,
                    end: iterator.extentCount - 1,
                }
            );
        }
    }

    private transformToDataStructure(datasets): Array<any> {
        if (datasets && !this.isDisposed) {
            return (datasets as Array<Array<any>>).map(dataset => {
                return {
                    pdoId: dataset[0],
                    name: dataset[1],
                };
            });
        }
        return [];
    }

    private evaluateOkayButtonEnabledState(): boolean {
        let aToBValue = this.aToB.value().toLowerCase();
        let bToAValue = this.bToA.value().toLowerCase();
        let targetTypeValue = (this.targetType.value() || '').toLowerCase();
        let nameValue = this.name.value().toLowerCase();

        let metaData = this.metaData;
        let sourceType = metaData.Types[metaData.SubjectPDTypeMeta];
        let isSelfAssociation = Utilities.equalsIgnoreCase(
            targetTypeValue,
            sourceType.Name
        );

        let state =
            Utilities.isNotBlank(nameValue) &&
            Utilities.isNotBlank(aToBValue) &&
            Utilities.isNotBlank(bToAValue) &&
            Utilities.isNotBlank(targetTypeValue) &&
            !_.contains(
                this.taken_AtoB_Names.map(e => e.toLowerCase()),
                aToBValue
            ) &&
            !_.contains(
                this.taken_BtoA_Names.map(e => e.toLowerCase()),
                bToAValue
            ) &&
            !_.contains(
                this.taken_DA_Names.map(e => e.toLowerCase()),
                nameValue
            ) &&
            !_.contains(
                this.taken_BtoA_NamesByTargetType[targetTypeValue],
                bToAValue
            ) &&
            (!isSelfAssociation ||
                !Utilities.equalsIgnoreCase(aToBValue, bToAValue));

        return state;
    }

    private defaultIteratorOperator = {
        memoryOptimizationEnabled: true,
        disablePrincipalFilter: null,
        historizationDates: null,
        includeAllPrincipals: null,
        includeBasePrincipals: null,
        includePrincipalFromSubject: null,
        includeSubPrincipals: null,
        onlySubPrincipals: null,
        principalFilter: null,
        searchObjectPositionRequest: null,
        showCurrentObjects: null,
        showHistoricedObjects: null,
        showObjectsWithoutPrincipal: null,
        visibleDeletedObjects: null,
        isGridIterator: false,
        hideConnectedElementsRequest: null,
    };

    private hasWhiteSpace(s) {
        return s.indexOf(' ') >= 0;
    }

    private loadResource(resourceId: string): string {
        return ResourceManager.getInstance().get(resourceId);
    }
}

export default CreateDynamicAssociationViewViewModel;
